'use client'

import { useEffect, useMemo, useState } from 'react'
import { createClient } from '@/utils/supabase/client'
import { cn } from '@/lib/utils'

type AudienceIngestStatus = 'pending' | 'claimed' | 'running' | 'succeeded' | 'failed' | 'partial'

interface AudienceIngestJob {
  id: string
  status: AudienceIngestStatus
  rows_total: number | null
  rows_processed: number | null
  rows_failed: number | null
  errors: unknown[] | null
}

const TERMINAL_STATUSES = new Set<AudienceIngestStatus>(['succeeded', 'failed', 'partial'])

function formatError(error: unknown): string {
  if (typeof error === 'string') return error
  if (!error || typeof error !== 'object') return String(error)

  const record = error as Record<string, unknown>
  const row = record.row ?? record.row_number ?? record.line
  const reason = record.reason ?? record.error ?? record.message
  if (row && reason) return `Row ${row}: ${String(reason)}`
  if (reason) return String(reason)
  return JSON.stringify(error)
}

function statusTone(status: AudienceIngestStatus): string {
  if (status === 'succeeded') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200'
  if (status === 'partial') return 'bg-amber-50 text-amber-700 border-amber-200'
  return 'bg-[#F3F2F1] text-[#5F5E61] border-atrium-border'
}

function dotTone(status: AudienceIngestStatus): string {
  if (status === 'succeeded') return 'bg-emerald-500'
  if (status === 'failed') return 'bg-red-500'
  if (status === 'partial') return 'bg-amber-500'
  return 'bg-accent-600'
}

export function AudienceIngestProgress({ jobId }: { jobId: string }) {
  const [job, setJob] = useState<AudienceIngestJob | null>(null)

  useEffect(() => {
    const supabase = createClient()
    let mounted = true

    const load = async () => {
      const { data } = await supabase
        .from('audience_ingest_jobs')
        .select('*')
        .eq('id', jobId)
        .single()

      if (mounted && data) {
        setJob(data as unknown as AudienceIngestJob)
      }
    }

    void load()

    const channel = supabase
      .channel(`audience_ingest_jobs:${jobId}`)
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'audience_ingest_jobs',
          filter: `id=eq.${jobId}`,
        },
        (payload: any) => {
          setJob(payload.new as AudienceIngestJob)
        },
      )
      .subscribe()

    return () => {
      mounted = false
      void supabase.removeChannel(channel)
    }
  }, [jobId])

  const rowsProcessed = job?.rows_processed ?? 0
  const rowsTotal = job?.rows_total ?? 0
  const rowsFailed = job?.rows_failed ?? 0
  const status = job?.status ?? 'pending'
  const isTerminal = TERMINAL_STATUSES.has(status)
  const percent = rowsTotal > 0 ? Math.min(100, Math.round((rowsProcessed / rowsTotal) * 100)) : 0
  const errors = useMemo(() => (Array.isArray(job?.errors) ? job.errors.slice(0, 3) : []), [job?.errors])

  return (
    <div className="mt-3 rounded-lg border border-atrium-border bg-atrium-sidebar p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-atrium-black">
            {isTerminal
              ? `Done - ${rowsProcessed.toLocaleString()} rows uploaded, ${rowsFailed.toLocaleString()} failed`
              : `Streaming ${rowsProcessed.toLocaleString()} / ${rowsTotal.toLocaleString()} rows to UA`}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize',
            statusTone(status),
          )}
        >
          {status}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-[#E8E6E3]" aria-label={`${percent}% complete`}>
        <div
          className={cn('h-full rounded-full transition-all duration-300', dotTone(status))}
          style={{ width: `${percent}%` }}
        />
      </div>

      {isTerminal && (
        <div className="mt-2 flex items-center gap-2 text-xs text-[#5F5E61]">
          <span className={cn('size-2 rounded-full', dotTone(status))} aria-hidden />
          <span>
            {status === 'succeeded'
              ? 'Upload complete'
              : status === 'partial'
                ? 'Upload completed with row errors'
                : 'Upload failed'}
          </span>
        </div>
      )}

      {isTerminal && errors.length > 0 && (
        <details className="mt-2 text-xs text-[#5F5E61]">
          <summary className="cursor-pointer font-medium text-atrium-black">Row errors</summary>
          <ul className="mt-1 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{formatError(error)}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
